import { z } from 'zod';

// ---------------------------------------------------------------------------
// G5: GET /v1/sitemap/variants — cursor-based pagination, 1000/page
// Stable ordering by (category_id, slug); cursor = base64(category_id + slug)
// ---------------------------------------------------------------------------

export const SitemapVariantsResponseSchema = z.object({
  items: z.array(z.object({
    category_slug: z.string(),
    variant_slug: z.string(),
    lang_code: z.string(),
    last_modified: z.string().datetime().nullable(),
  })),
  next_cursor: z.string().nullable(),
});
export type SitemapVariantsResponse = z.infer<typeof SitemapVariantsResponseSchema>;

// ---------------------------------------------------------------------------
// GET /v1/sitemap/interiors — cursor-based pagination, 500/page
// Stable ordering by (wallpaper_id, id); cursor = base64(wallpaper_id + ":" + id)
//
// Returns ready interiors with composed alt/caption for image-sitemap emission.
// The api/ endpoint computes caption via composeInteriorAlt so the client can
// attach images to product URLs without extra joins.
// ---------------------------------------------------------------------------

export const SitemapInteriorsItemSchema = z.object({
  /** Public image URL (storage CDN path). */
  image_url: z.string().url(),
  /**
   * Composed caption = alt text: "fototapeta <wallpaper-name> — <room-ending>".
   * Identical to alt in Phase-2; divergence is downstream (Кусок D).
   */
  caption: z.string(),
  /** Wallpaper's category i18n slug (used to build the owning product URL). */
  wallpaper_slug: z.string(),
  /** Room category i18n slug. */
  category_slug: z.string(),
  /** BCP-47 locale for this item. */
  lang_code: z.string(),
  /** ISO-8601 timestamp from wallpapers_interiors.updated_at. */
  last_modified: z.string().datetime().nullable(),
});
export type SitemapInteriorsItem = z.infer<typeof SitemapInteriorsItemSchema>;

export const SitemapInteriorsResponseSchema = z.object({
  items: z.array(SitemapInteriorsItemSchema),
  next_cursor: z.string().nullable(),
});
export type SitemapInteriorsResponse = z.infer<typeof SitemapInteriorsResponseSchema>;

// ---------------------------------------------------------------------------
// GET /v1/sitemap/originals — cursor-based pagination, 500/page
// Stable ordering by (category_id, slug); cursor = base64(category_id + ":" + slug)
//
// Returns ORIG image entries (one per wallpaper × lang) from wallpapers.original_path.
// Reuses SitemapInteriorsItem shape (additive — does not break existing interiors contract).
// caption = composeOrigAlt; category_slug is unused ("") for ORIG items.
// ---------------------------------------------------------------------------

/** Alias — ORIG items carry the same shape as interior items. */
export const SitemapOriginalsItemSchema = SitemapInteriorsItemSchema;
export type SitemapOriginalsItem = SitemapInteriorsItem;

export const SitemapOriginalsResponseSchema = z.object({
  items: z.array(SitemapOriginalsItemSchema),
  next_cursor: z.string().nullable(),
});
export type SitemapOriginalsResponse = z.infer<typeof SitemapOriginalsResponseSchema>;
